import React, {useEffect, useRef} from 'react'
import Spinner from '../Spinner/Spinner'
import styled from 'styled-components'
import {fetchSvgData} from '../../utils/drawing'
import {useMarker} from './hooks/useMarker'
import {useAssetHighlight} from './hooks/useAssetHighlight'
import {_L, useLocaleState} from '../../context/LocaleContext'
import {
  useDrawingDispatch,
  setDrawingStatus
} from '../../context/DrawingContext'
import {zoom, zoomIdentity} from 'd3-zoom'
import {select, event, remove} from 'd3-selection'

import {useViewFetch} from '../../utils/fetch'

const Drawing = ({
  activeFloor,
  onHighlightClick,
  marker,
  highlightAssets,
  highlights
}) => {
  const svgEl = useRef(null)
  const localeState = useLocaleState()
  const drawingDispatch = useDrawingDispatch()

  const {blId, flId} = activeFloor

  const {data, isLoading: loading} = useViewFetch({
    api: fetchSvgData,
    parameters: {blId, flId},
    onChangeOf: [blId, flId],
    initialState: {
      svgData: null,
      svgAttributes: {viewBox: '0 0 0 0'},
      hasDrawing: false
    }
  })
  
  const {svgData, attributes: svgAttributes, hasDrawing} = data

  const isLoaded = !loading

  useEffect(
    () => {
      if (hasDrawing && isLoaded) {
		const mapNavInstr = document.getElementById("mapnav_instructions")
		mapNavInstr.innerHTML = "Use arrow keys to pan; plus or minus to zoom; r or equal to reset pan and zoom"
        
		const d3svg = select(svgEl.current)
		d3svg
			.attr("aria-label","Image of Floor "+flId)
			.append("title").attr("id", "svgNavInstructions").text("Use arrow keys to pan; plus or minus to zoom; r or equal to reset pan and zoom")
			.attr("aria-describedby","svgNavInstructions")
		
		const dfltViewBox = d3svg.select("#defaultView").attr("viewBox")
		
        const g = d3svg.select('#viewer')

        d3svg.style('width', 'auto').style('height', '100vh')
        //d3svg.call(ABDRAWING.viewBoxZoom())
        let rmIdLayer = document.getElementById("layer-A-AREA-RMID-text")
        if(rmIdLayer != null){
        	rmIdLayer.remove()
        }
        let rmAssetLayer = document.getElementById("rm-assets")
        if(rmAssetLayer != null){
        	rmAssetLayer.style.strokeOpacity = 0
        	rmAssetLayer.style.fillOpacity = 0
        }
        let grosAssetLayer = document.getElementById("gros-assets")
        if(grosAssetLayer != null){
        	grosAssetLayer.setAttribute("stroke-width","0")
        }
        let rmLayer = document.getElementById("layer-RM")
        if(rmLayer != null){
        	rmLayer.style.display = "none"
        }
        let rmDolLayer = document.getElementById("layer-RM-dol-TXT")
        if(rmDolLayer != null){
        	rmDolLayer.style.display = "none"
        }
        let adaTextLayer = document.getElementById("layer-TEXT-ADA")
        if(adaTextLayer){
        	adaTextLayer.setAttribute("stroke-width","0.658%")
        }
        let adaLayer = document.getElementById("layer-TEXT-ADA-text")
        if(adaLayer){
        	adaLayer.setAttribute("font-weight","bold")
        }
		/* attempt to make height of text on "layer-TEXT-ADA-text" more consistent*/
		/*
			let currFontSize = adaLayer.getAttribute("font-size")
			if (!currFontSize) {
				currFontSize = adaLayer.parentElement.getAttribute("font-size")
			}
			let adaLayerChild = adaLayer.firstElementChild
			if(adaLayerChild && currFontSize) {
				let childHeight = adaLayerChild.getBoundingClientRect().height
				let heightFactor = (20.0 / childHeight)
				let newHeight = (currFontSize * heightFactor)
				newHeight = newHeight.toString()
				adaLayer.setAttribute("font-size",newHeight)
			}
		*/
        let eqAssetLayer = document.getElementById("eq-assets")
        if(eqAssetLayer){
        	eqAssetLayer.style.display = "none"
        }
        let eqLayer = document.getElementById("layer-EQ")
        if(eqLayer){
        	eqLayer.style.display = "none"
        }
        let eqLabelsLayer = document.getElementById("eq-labels")
        if(eqLabelsLayer){
        	eqLabelsLayer.style.display = "none"
        }
        let grosLabelsLayer = document.getElementById("gros-labels")
        if(grosLabelsLayer){
        	grosLabelsLayer.style.display = "none"
        }
        
        const zoomer = zoom()
          .on('zoom', function() {
            g.attr('transform', event.transform)
          })
          .scaleExtent([0.5, 20])

        d3svg.call(zoomer)
		
		const zoomInPct = 1.1
		const zoomOutPct = 0.9
		
		/* 
		const wheelPct = 0.05
		d3svg.on('wheel', function() {
			const wheelYDelta = (-event.deltaY / event.deltaY) * wheelPct
			zoomer.wheelDelta(wheelYDelta)
		})
		 */
	
		d3svg.on('keydown', function() {
			const panPct = 0.05
			const [x, y, width, height] = d3svg.attr('viewBox').split(' ');
			const dx = Number(width) * panPct;
			const dy = Number(height) * panPct;
			switch(event.key) {
				case "ArrowDown":
					d3svg.attr('viewBox', `${x} ${Number(y) + dy} ${width} ${height}`)
					break;
				case "ArrowUp":
					d3svg.attr('viewBox', `${x} ${Number(y) - dy} ${width} ${height}`)
					break;
				case "ArrowLeft":
					d3svg.attr('viewBox', `${Number(x) - dx} ${y} ${width} ${height}`)
					break;
				case "ArrowRight":
					d3svg.attr('viewBox', `${Number(x) + dx} ${y} ${width} ${height}`)
					break;
				case "+":
					d3svg.call(zoomer.scaleBy, zoomInPct)
					break;
				case "-":
					d3svg.call(zoomer.scaleBy, zoomOutPct)
					break;
				case "r":
				case "=":
					d3svg.attr('viewBox', dfltViewBox)
					d3svg.call(zoomer.transform, zoomIdentity)
					break;
			}
		})
		const zoomIn = select(document.getElementById("zoomIn"))
		zoomIn
			.on("click", function() {
				d3svg.call(zoomer.scaleBy, zoomInPct)
			})
			.on("keydown", function() {
				if (event.key == "Enter") {
						d3svg.call(zoomer.scaleBy, zoomInPct)
				}	
			})
			.attr('aria-label', "Zoom In")
		const zoomOut = select(document.getElementById("zoomOut"))
		zoomOut
			.on("click", function() {
				d3svg.call(zoomer.scaleBy, zoomOutPct)
			})
			.on("keydown", function() {
				if (event.key == "Enter") {
					d3svg.call(zoomer.scaleBy, zoomOutPct)
				}	
			})
			.attr('aria-label', "Zoom Out")
		const zoomReset = select(document.getElementById("zoomReset"))
		zoomReset
			.on("click", function() {
				d3svg.attr('viewBox', dfltViewBox)
				d3svg.call(zoomer.transform, zoomIdentity)
			})
			.on("keydown", function() {
				if (event.key == "Enter") {
					d3svg.attr('viewBox', dfltViewBox)
					d3svg.call(zoomer.transform, zoomIdentity)
				}	
			})
			.attr('aria-label', "Reset Zoom")
		  }

      if (isLoaded) {
        setDrawingStatus({
          dispatch: drawingDispatch,
          hasSvgData: hasDrawing,
          blId,
          flId
        })
      }
    },
    [hasDrawing, isLoaded, blId, flId, drawingDispatch]
  )

  useAssetHighlight({
    svgElement: svgEl.current,
    isLoaded,
    highlightAssets,
    highlights,
    onHighlightClick
  })

  useMarker({marker, svgElement: svgEl.current, isLoaded})

  const renderLoadingIndicator = () =>
    loading ? (
      <SpinnerContainer>
        <Spinner color="#3395fe" />
      </SpinnerContainer>
    ) : null

  const renderNoDrawingMessage = () =>
    !loading && !svgData ? (
      <EmptyMessage>
        {_L({key: 'Floor plan is not available', localeState})}
      </EmptyMessage>
    ) : null

  const renderDrawing = () =>
    svgData && !loading ? (
      <SvgContainer tabIndex="0" role="application"
        viewBox={svgAttributes.viewBox}
        dangerouslySetInnerHTML={{__html: svgData}}
        ref={svgEl}
      />
    ) : null

  return (
    <Container>
      {renderDrawing()}
	  <MapNavInstructions id="mapnav_instructions"/>
      {renderLoadingIndicator()}
      {renderNoDrawingMessage()}
    </Container>
  )
}

export default Drawing

const MapNavInstructions = styled.div`
  display: none;
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: center;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 0px 10px 0px 10px;
  max-height: 100%;
  max-width: 100%;
  box-sizing: border-box;
  height: calc(100vh - 200px);
  width: auto;
  
  @media (max-width: 768px) {
	  height: calc(100vh - 325px);
  }
  
  @media (max-width: 420px) {
	  height: calc(100vh - 315px);
  }
  
  @media print {
	  height: 100%
  }
  
`
/* 2022114 */
/* 
const Container = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 0px 16px 0px 16px;
  max-height: 100%;
  max-width: 100%;
  box-sizing: border-box;
  height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
	  height: calc(100vh - 325px);
  }
  
  @media (max-width: 420px) {
	  height: calc(100vh - 315px);
  }
`
 */

const SvgContainer = styled.svg`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: center;
  justify-content: center;
  overflow: hidden;
  max-height: 96%;
  max-width: 96%;
  box-sizing: border-box;
  
  :focus{
	  outline: 3px solid #FF9900;
  }
`

/* 2022114 */
/* 
const SvgContainer = styled.svg`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  max-height: 96%;
  max-width: 96%;
  box-Sixing: 'border-box';
  
  :focus{
	  outline: 3px solid #FF9900;
  }
`
 */

/* 
const SvgContainer = styled.svg`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-content: center;
  height: 96%;
  
  :focus{
	  outline: 3px solid #FF9900;
  }
  
  @media print {
	height: 100%;
  }
`
 */

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
`
const EmptyMessage = styled.div`
  font-size: 20px;
  text-align: center;
  justify-content: center;
  flex: 1;
`
